import { Import } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';
import { getRouteApi } from '@tanstack/react-router';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import { useHasEverPairedDeviceSuspense } from 'src/api/queries';
import googleChromeLogo from 'src/assets/icons/googleChrome.svg';
import windowsColorLogo from 'src/assets/icons/windowsColor.svg';
import { Button, CardContent, FAQ, FAQLine, Sheet } from 'src/components';
import { SUPPORT_URL } from 'src/utils/urls/supportUrls';
import { URLS } from 'src/utils/urls/urls';

import { Trans, useLingui } from '@lingui/react/macro';
import { CHROME_EXTENSION_URL, OFFICE_EXTENSION_URL } from '../utils/constants';
import { DeviceTable } from './DeviceTable';
import { OneTimeCode } from './OneTimeCode';

const routeApi = getRouteApi('/_auth/_layout/device/browser');

export const Browser = () => {
  const { t } = useLingui();
  const { showOtp } = routeApi.useSearch();
  // Note: this should probably only be enabled if showOtp is true
  // Will take separation of OTP logic into subroute similar to how apps handles it in another refactor
  const hasEverPairedDevice = useHasEverPairedDeviceSuspense();

  return (
    <div className="flex flex-col gap-16 lm:gap-24">
      <Sheet>
        {!showOtp ? (
          <>
            <CardContent className="my-12 flex flex-row items-center justify-between lm:mt-0">
              <div className="flex flex-row items-center">
                <img
                  src={googleChromeLogo}
                  alt="Google Chrome"
                  className="mr-16"
                  width={32}
                  height={32}
                />
                <Typography variant="heading-subtle-2xl" as="h2">
                  <Trans>Google Chrome</Trans>
                </Typography>
              </div>
              <Button
                as="a"
                to={CHROME_EXTENSION_URL}
                className="hidden md:flex"
                size="medium"
                variant="primary"
                analytics={{
                  action: 'download chrome extension',
                  location: ComponentLocations.DEVICE.READ_ON_REMARKABLE,
                  text: 'Get extension',
                }}
              >
                <Import size={24} />
                <span>
                  <Trans>Get extension</Trans>
                </span>
              </Button>
            </CardContent>
            <CardContent borderTop className="flex flex-col gap-24 text-left">
              <p>
                <Trans>
                  Easily send web articles straight to your paper tablet as
                  Notebook or EPUB, so you can read with focus
                </Trans>
              </p>
              <Button
                size="medium"
                data-cy="download-chrome-extension-button"
                as="a"
                to={CHROME_EXTENSION_URL}
                className="md:hidden"
                variant="primary"
              >
                <Import size={24} />
                <span>
                  <Trans>Get extension</Trans>
                </span>
              </Button>
            </CardContent>
          </>
        ) : (
          <CardContent>
            <OneTimeCode
              successRedirectUrl={URLS.DEVICE_BROWSER}
              description="Enter this one-time code in your Chrome browser window to pair it with your device and start saving web articles to read."
              allowPairing={hasEverPairedDevice.data}
              deviceTypeString="browser extension"
              componentLocation={ComponentLocations.DEVICE.READ_ON_REMARKABLE}
            />
          </CardContent>
        )}
      </Sheet>

      {!showOtp && (
        <>
          <Sheet>
            <CardContent className="my-12 flex flex-row items-center justify-between lm:mt-0">
              <div className="flex flex-row items-center">
                <img
                  src={windowsColorLogo}
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                  alt={t`Microsoft Office`}
                  className="mr-16"
                  width={32}
                  height={32}
                />
                <Typography variant="heading-subtle-2xl" as="h2">
                  <Trans>Microsoft Office</Trans>
                </Typography>
              </div>
              <Button
                as="a"
                size="medium"
                className="hidden md:flex"
                to={OFFICE_EXTENSION_URL}
                variant="primary"
              >
                <Import size={24} />
                <span>
                  <Trans>Get add-in</Trans>
                </span>
              </Button>
            </CardContent>
            <CardContent borderTop className="flex flex-col gap-24 text-left">
              <p>
                <Trans>
                  Send Word documents or PowerPoint slides direct to your device
                  as PDFs
                </Trans>
              </p>
              <Button
                data-cy="download-office-add-in-button"
                as="a"
                size="medium"
                className="md:hidden"
                to={OFFICE_EXTENSION_URL}
                variant="primary"
              >
                <Import size={24} />
                <span>
                  <Trans>Get add-in</Trans>
                </span>
              </Button>
            </CardContent>
          </Sheet>

          <DeviceTable deviceType="read-on-remarkable" />

          <FAQ>
            <FAQLine
              text={t`How do I send web articles to my reMarkable?`}
              target={SUPPORT_URL.ARTICLES.ROR_GOOGLE_CHROME_EXTENSION}
            />
            <FAQLine
              text={t`How do I send Word and PowerPoint documents to my reMarkable?`}
              target={SUPPORT_URL.ARTICLES.ROR_WORD_AND_POWERPOINT}
            />
            <FAQLine
              text={t`Where can I get help using Read on reMarkable?`}
              target={SUPPORT_URL.ARTICLES.ROR_WORD_AND_POWERPOINT}
            />
            <FAQLine
              text={t`Can I read Kindle ebooks on reMarkable 2?`}
              target={SUPPORT_URL.ARTICLES.KINDLE_ON_REMARKABLE}
            />
            <FAQLine
              text={t`How do I get ebooks on my device?`}
              target={SUPPORT_URL.ARTICLES.EBOOKS_ON_REMARKABLE}
            />
            <FAQLine
              text={t`Which file types are supported on reMarkable?`}
              target={SUPPORT_URL.ARTICLES.FILETYPES_REMARKABLE}
            />
            <FAQLine
              text={t`How do I connect to the reMarkable cloud?`}
              target={SUPPORT_URL.ARTICLES.PAIR_REMARKABLE_WITH_CLOUD}
            />
          </FAQ>
        </>
      )}
    </div>
  );
};
